import { useEffect, useRef, useState } from 'react';
import { api, API } from '../lib/api';
import { X, Loader2, Volume2, Languages } from 'lucide-react';

/**
 * SyllablePopover — tap-to-hear + tap-to-mean helper for a single word.
 *
 * Iter-51.5: added a Meaning section under the Pronunciation section.
 *   • The two sections are visually separate (bordered card + label chip)
 *     so a child never confuses "how to say it" with "what it means".
 *   • Meaning fetches on demand (single POST /api/lessons/meaning with
 *     mode='word') so the popover still opens instantly on tap.
 *   • Meaning text is Hindi; the small speaker replays it in Hindi voice
 *     (no per-section speed slider — the meaning is short).
 *
 * Props:
 *   word:        the tapped word (string, punctuation stripped is fine)
 *   language:    'hi' | 'en' — same as the surrounding chunk
 *   ttsPlay:     (text, opts) => Promise — shared useTts.play
 *   voiceGender: 'female' | 'male'
 *   onClose:     () => void
 */
// Iter-52.0 — Slider REMOVED. iter-51.98 lowered the slider min to
// 0.55 for parent-parity with the Very-Slow reading tier, but real-
// world testing on 7 previously-tricky words (came, played, learn,
// fellow, rabbit, monkey, watched) showed the same word sounds
// audibly different at different ElevenLabs `speed=` settings (vowel
// length ratios + intonation curves change). To eliminate that
// inconsistency for young readers, the popover now plays every word
// at a SINGLE canonical el_speed = 0.85 — the value that produced
// the clearest pronunciation most consistently across the test set.
// Read-to-Me's own slider (0.30x → 1.20x) is UNTOUCHED — full-chunk
// narration still lets parents choose the pace.
const POPOVER_FIXED_SPEED = 0.85;
const syllableCache = new Map();
const meaningCache = new Map();
const wordAudioCache = new Map();
// Iter-52.0 — kept as a module-level constant reference for the
// backend slice cache-key so a session's cache stays stable when
// the popover is closed and reopened for the same word.
const lastChosenSpeed = POPOVER_FIXED_SPEED;

export default function SyllablePopover({ word, language, sentence, wordIndex, ttsPlay, voiceGender, onClose }) {
  const [state, setState] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [syllables, setSyllables] = useState([]);
  const [full, setFull] = useState(word);
  // Iter-52.0 — Slider removed; speed is a fixed constant now. Kept
  // as local state so the `Play again` button and the slice cache
  // still receive the same value if we ever want to A/B tweak it
  // in a future iteration.
  const speed = POPOVER_FIXED_SPEED;
  const [playing, setPlaying] = useState(false);

  const [meaningState, setMeaningState] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'
  // Iter-52.3 — bilingual meaning shape. `srcExplanation` is in the same
  // language as the tapped word; `otherExplanation` is in the OTHER
  // language. Both are shown side-by-side with their own play buttons.
  const [srcExplanation, setSrcExplanation] = useState('');
  const [otherExplanation, setOtherExplanation] = useState('');
  const [equivalent, setEquivalent] = useState('');
  // Iter-52.21 — bilingual example sentences shown below each
  // explanation. `srcExample` is in the SAME language as the tapped
  // word; `otherExample` is the cross-language example.
  const [srcExample, setSrcExample] = useState('');
  const [otherExample, setOtherExample] = useState('');
  const [meaningPlaying, setMeaningPlaying] = useState({ src: false, other: false });

  const cancelledRef = useRef(false);

  // Iter-51.88 — Prefer the sentence-slice audio for natural single-word
  // playback. Falls back to the isolated-word TTS if the sentence is
  // missing (e.g. legacy caller) or the backend can't locate the word
  // in the alignment.
  const playSlicedWord = async (atSpeed) => {
    if (cancelledRef.current) return false;
    if (!sentence || sentence.length < 3) return false;
    const cacheKey = `${language}::${voiceGender}::${sentence}::${word}::${wordIndex}::${atSpeed}`;
    const cachedUrl = wordAudioCache.get(cacheKey);
    setPlaying(true);
    try {
      let objUrl = cachedUrl;
      let playbackRate = 1.0;
      if (!objUrl) {
        const resp = await fetch(`${API}/tts/word`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            word, sentence,
            language, voice_gender: voiceGender,
            speed: atSpeed, word_index: wordIndex || 0,
          }),
        });
        if (!resp.ok) throw new Error(`word-tts ${resp.status}`);
        const rateHdr = parseFloat(resp.headers.get('X-Client-Playback-Rate') || '1.0');
        if (!Number.isNaN(rateHdr)) playbackRate = rateHdr;
        const blob = await resp.blob();
        objUrl = URL.createObjectURL(blob);
        wordAudioCache.set(cacheKey, objUrl);
      }
      const audio = new Audio(objUrl);
      audio.playbackRate = playbackRate;
      await new Promise((resolve) => {
        audio.addEventListener('ended', resolve, { once: true });
        audio.addEventListener('error', resolve, { once: true });
        audio.play().catch(() => resolve());
      });
      if (!cancelledRef.current) setPlaying(false);
      return true;
    } catch {
      if (!cancelledRef.current) setPlaying(false);
      return false;
    }
  };

  const playWord = async (spoken, atSpeed) => {
    if (cancelledRef.current) return;
    // Try sentence-sliced audio first — natural prosody, no stretch.
    const sliced = await playSlicedWord(atSpeed);
    if (sliced) return;
    // Fallback: isolated-word TTS (may sound slightly artificial but
    // is guaranteed to produce audio).
    setPlaying(true);
    try { await ttsPlay(spoken, { language, speed: atSpeed, voice_gender: voiceGender }); } catch {}
    if (!cancelledRef.current) setPlaying(false);
  };

  const playMeaning = async (which) => {
    if (cancelledRef.current) return;
    // Iter-52.3 — bilingual: two independent play buttons.
    //   which='src'   → speak the source-language explanation in `language`
    //   which='other' → speak the cross-language explanation in the OTHER language
    const otherLang = language === 'en' ? 'hi' : 'en';
    const speakLang = which === 'src' ? language : otherLang;
    const text = which === 'src' ? srcExplanation : otherExplanation;
    if (!text) return;
    setMeaningPlaying(p => ({ ...p, [which]: true }));
    try { await ttsPlay(text, { language: speakLang, speed: 0.85, voice_gender: voiceGender }); } catch {}
    if (!cancelledRef.current) setMeaningPlaying(p => ({ ...p, [which]: false }));
  };

  const loadMeaning = async () => {
    const key = `${language}::${word}`;
    const cached = meaningCache.get(key);
    if (cached) {
      setSrcExplanation(cached.srcExplanation || '');
      setOtherExplanation(cached.otherExplanation || '');
      setEquivalent(cached.equivalent || '');
      // Iter-52.21 — example sentences from the in-memory cache.
      // Older cached entries (pre-iter-52.21) lack these fields so
      // they'll be empty strings — the render block simply hides
      // the "Example:" line in that case; the next tap of the same
      // word (fresh api hit) will backfill.
      setSrcExample(cached.srcExample || '');
      setOtherExample(cached.otherExample || '');
      setMeaningState('ready');
      // Auto-play the source-language side (matches the tapped word).
      if (cached.srcExplanation) {
        setMeaningPlaying(p => ({ ...p, src: true }));
        try { await ttsPlay(cached.srcExplanation, { language, speed: 0.85, voice_gender: voiceGender }); } catch {}
        if (!cancelledRef.current) setMeaningPlaying(p => ({ ...p, src: false }));
      }
      return;
    }
    setMeaningState('loading');
    try {
      const { data } = await api.post('/lessons/meaning', { text: word, language, mode: 'word' });
      if (cancelledRef.current) return;
      const src = (data.explanation || '').trim();
      const other = (data.explanation_other || '').trim();
      const eq = (data.equivalent || '').trim();
      // Iter-52.21 — pull example sentences alongside the explanation.
      // Backend generates both in the SAME LLM call so this is a
      // zero-latency addition. `example` matches the tapped-word
      // language; `example_other` is the cross-language equivalent.
      const ex = (data.example || '').trim();
      const exOther = (data.example_other || '').trim();
      if (!src && !other) throw new Error('empty');
      meaningCache.set(key, {
        srcExplanation: src, otherExplanation: other, equivalent: eq,
        srcExample: ex, otherExample: exOther,
      });
      setSrcExplanation(src);
      setOtherExplanation(other);
      setEquivalent(eq);
      setSrcExample(ex);
      setOtherExample(exOther);
      setMeaningState('ready');
      // Auto-play the source-language explanation on first open.
      if (src) {
        setMeaningPlaying(p => ({ ...p, src: true }));
        try { await ttsPlay(src, { language, speed: 0.85, voice_gender: voiceGender }); } catch {}
        if (!cancelledRef.current) setMeaningPlaying(p => ({ ...p, src: false }));
      }
    } catch {
      if (cancelledRef.current) return;
      setMeaningState('error');
    }
  };

  useEffect(() => {
    // Iter-51.33 — Bug fix for "two voices at once on first tap".
    //
    // Previous impl used a shared `cancelledRef.current` flag. Under
    // React.StrictMode (enabled in index.js), effects double-invoke on
    // mount → the cleanup between runs sets the flag to `true`, but the
    // second run immediately resets it back to `false` before the FIRST
    // run's `await api.post(...)` resolves. Both async completions then
    // pass the "if (cancelled) return;" guard and fire playWord →
    // TWO simultaneous TTS fetches → two overlapping audio elements.
    //
    // Fix: use a per-effect-run local `cancelled` variable. Each async
    // closure captures its OWN flag; StrictMode's cleanup toggles ONLY
    // that closure's flag, leaving the second run's flag untouched.
    // Consequence: exactly one playWord call reaches ttsPlay on first
    // open (and on every subsequent open).
    let cancelled = false;
    cancelledRef.current = false;
    const playIfActive = async (spoken) => {
      if (cancelled) return;
      setPlaying(true);
      // Iter-51.88 — prefer sliced sentence audio on auto-open too.
      const sliced = await (async () => {
        if (!sentence || sentence.length < 3) return false;
        const cacheKey = `${language}::${voiceGender}::${sentence}::${word}::${wordIndex}::${lastChosenSpeed}`;
        try {
          let objUrl = wordAudioCache.get(cacheKey);
          let playbackRate = 1.0;
          if (!objUrl) {
            const resp = await fetch(`${API}/tts/word`, {
              method: 'POST', credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                word, sentence,
                language, voice_gender: voiceGender,
                speed: lastChosenSpeed, word_index: wordIndex || 0,
              }),
            });
            if (!resp.ok) throw new Error(`word-tts ${resp.status}`);
            const rateHdr = parseFloat(resp.headers.get('X-Client-Playback-Rate') || '1.0');
            if (!Number.isNaN(rateHdr)) playbackRate = rateHdr;
            const blob = await resp.blob();
            objUrl = URL.createObjectURL(blob);
            wordAudioCache.set(cacheKey, objUrl);
          }
          if (cancelled) return true;
          const audio = new Audio(objUrl);
          audio.playbackRate = playbackRate;
          await new Promise((resolve) => {
            audio.addEventListener('ended', resolve, { once: true });
            audio.addEventListener('error', resolve, { once: true });
            audio.play().catch(() => resolve());
          });
          return true;
        } catch {
          return false;
        }
      })();
      if (!sliced) {
        try { await ttsPlay(spoken, { language, speed: lastChosenSpeed, voice_gender: voiceGender }); } catch {}
      }
      if (!cancelled && !cancelledRef.current) setPlaying(false);
    };
    const key = `${language}::${word}`;
    (async () => {
      const cached = syllableCache.get(key);
      if (cached) {
        if (cancelled) return;
        setSyllables(cached.syllables || []);
        setFull(cached.full || word);
        setState('ready');
        playIfActive(cached.full || word);
        return;
      }
      try {
        const { data } = await api.post('/lessons/syllabify', { word, language });
        if (cancelled) return;
        syllableCache.set(key, data);
        setSyllables(data.syllables || []);
        setFull(data.full || word);
        setState('ready');
        playIfActive(data.full || word);
      } catch {
        if (cancelled) return;
        // Even if syllabify fails, we can still voice the whole word.
        setSyllables([]);
        setFull(word);
        setState('ready');
        playIfActive(word);
      }
    })();
    return () => { cancelled = true; cancelledRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word, language]);

  return (
    <div data-testid="syllable-popover" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
      onClick={onClose}>
      <div className="bg-white rounded-3xl p-5 w-full max-w-md shadow-2xl relative max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button data-testid="syllable-close" onClick={onClose}
          className="absolute right-3 top-3 text-slate-400 hover:text-slate-700 p-1"><X size={20}/></button>

        {state === 'loading' && (
          <div className="py-8 flex items-center justify-center text-slate-500">
            <Loader2 size={20} className="animate-spin mr-2"/> Loading "{word}"…
          </div>
        )}

        {state === 'ready' && (
          <>
            {/* ── SECTION 1 — Pronunciation ─────────────────────────── */}
            <div data-testid="pronunciation-section" className="rounded-2xl border-2 border-[#FFDDBB] bg-gradient-to-b from-[#FFF7ED] to-white p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-[#FF6B1A] text-white flex items-center justify-center">
                  <Volume2 size={14} strokeWidth={2.8}/>
                </div>
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#FF6B1A]">Pronunciation</span>
              </div>

              {/* Dictionary syllables — phonics visual only, no per-tile audio. */}
              {syllables.length > 1 && (
                <div data-testid="syllable-tiles" className="flex flex-wrap items-center gap-2 mb-3">
                  {syllables.map((s, i) => (
                    <span key={i} data-testid={`syllable-tile-${i}`}
                      className="px-3 py-2 rounded-2xl font-extrabold text-lg border-2 bg-white text-[#2B2140] border-slate-200">
                      {s}
                    </span>
                  ))}
                </div>
              )}

              <div data-testid="syllable-full-word"
                className={`text-3xl font-extrabold text-center py-4 rounded-2xl border-2 transition-colors
                  ${playing ? 'bg-[#FFEDD5] border-[#FF6B1A]' : 'bg-white border-slate-200'}`}>
                {full}
              </div>

              {/* Iter-52.0 — Slider removed; every tap plays at the
                  fixed 0.85x reference speed for pronunciation
                  consistency across words. */}

              <button data-testid="syllable-replay" onClick={() => playWord(full, speed)} disabled={playing}
                className="mt-3 w-full bg-[#5B8DEF] text-white font-bold py-2.5 rounded-full inline-flex items-center justify-center gap-2 text-sm disabled:opacity-60">
                {playing ? <><Loader2 size={16} className="animate-spin"/> Playing…</>
                         : <><Volume2 size={16} strokeWidth={2.8}/> Play again</>}
              </button>
            </div>

            {/* ── SECTION 2 — Meaning ───────────────────────────────── */}
            <div data-testid="meaning-section" className="mt-4 rounded-2xl border-2 border-[#DBEAFE] bg-gradient-to-b from-[#EFF6FF] to-white p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-[#5B8DEF] text-white flex items-center justify-center">
                  <Languages size={14} strokeWidth={2.8}/>
                </div>
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#5B8DEF]">Meaning</span>
              </div>

              {meaningState === 'idle' && (
                <button data-testid="meaning-fetch-btn" onClick={loadMeaning}
                  className="w-full bg-[#5B8DEF] text-white font-bold py-2.5 rounded-full inline-flex items-center justify-center gap-2 text-sm">
                  <Languages size={16} strokeWidth={2.8}/> Show meaning
                </button>
              )}
              {meaningState === 'loading' && (
                <div className="py-4 flex items-center justify-center text-slate-500 text-sm">
                  <Loader2 size={16} className="animate-spin mr-2"/> Finding meaning…
                </div>
              )}
              {meaningState === 'ready' && (
                <div data-testid="meaning-ready" className="space-y-3">
                  {/* Source-language block — SAME language as the tapped word. */}
                  {srcExplanation && (
                    <div data-testid="meaning-src-block"
                      className="flex items-start gap-3 bg-white/70 rounded-2xl p-3 border border-[#DBEAFE]">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-extrabold uppercase tracking-wider text-[#5B8DEF] mb-1">
                          {language === 'hi' ? 'हिंदी' : 'English'}
                        </div>
                        <div data-testid="meaning-src-text"
                          className={`text-sm font-bold text-[#2B2140] leading-snug break-words ${language === 'hi' ? 'font-hi' : ''}`}>
                          {word} — {srcExplanation}
                        </div>
                        {/* Iter-52.21 — same-language example sentence. */}
                        {srcExample && (
                          <div data-testid="meaning-src-example"
                            className={`text-xs italic text-[#2B2140]/70 mt-1.5 leading-snug break-words ${language === 'hi' ? 'font-hi' : ''}`}>
                            <span className={`not-italic font-extrabold uppercase tracking-wider text-[10px] text-[#5B8DEF] mr-1 ${language === 'hi' ? 'font-hi' : ''}`}>
                              {language === 'hi' ? 'उदाहरण:' : 'Example:'}
                            </span>
                            {srcExample}
                          </div>
                        )}
                      </div>
                      <button data-testid="meaning-play-src-btn" onClick={() => playMeaning('src')}
                        disabled={meaningPlaying.src}
                        className="w-10 h-10 rounded-full bg-[#5B8DEF] text-white flex items-center justify-center shrink-0 disabled:opacity-60"
                        aria-label={language === 'hi' ? 'Play Hindi explanation' : 'Play English explanation'}>
                        {meaningPlaying.src ? <Loader2 size={16} className="animate-spin"/> : <Volume2 size={16} strokeWidth={2.8}/>}
                      </button>
                    </div>
                  )}
                  {/* Cross-language block — the OTHER language (equivalent + full explanation). */}
                  {otherExplanation && (
                    <div data-testid="meaning-other-block"
                      className="flex items-start gap-3 bg-white/70 rounded-2xl p-3 border border-[#DBEAFE]">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-extrabold uppercase tracking-wider text-[#5B8DEF] mb-1">
                          {language === 'hi' ? 'English' : 'हिंदी'}
                        </div>
                        <div data-testid="meaning-other-text"
                          className={`text-sm font-bold text-[#2B2140] leading-snug break-words ${language === 'hi' ? '' : 'font-hi'}`}>
                          {equivalent ? `${equivalent} — ` : ''}{otherExplanation}
                        </div>
                        {/* Iter-52.21 — cross-language example sentence. */}
                        {otherExample && (
                          <div data-testid="meaning-other-example"
                            className={`text-xs italic text-[#2B2140]/70 mt-1.5 leading-snug break-words ${language === 'hi' ? '' : 'font-hi'}`}>
                            <span className={`not-italic font-extrabold uppercase tracking-wider text-[10px] text-[#5B8DEF] mr-1 ${language === 'hi' ? '' : 'font-hi'}`}>
                              {language === 'hi' ? 'Example:' : 'उदाहरण:'}
                            </span>
                            {otherExample}
                          </div>
                        )}
                      </div>
                      <button data-testid="meaning-play-other-btn" onClick={() => playMeaning('other')}
                        disabled={meaningPlaying.other}
                        className="w-10 h-10 rounded-full bg-[#5B8DEF] text-white flex items-center justify-center shrink-0 disabled:opacity-60"
                        aria-label={language === 'hi' ? 'Play English explanation' : 'Play Hindi explanation'}>
                        {meaningPlaying.other ? <Loader2 size={16} className="animate-spin"/> : <Volume2 size={16} strokeWidth={2.8}/>}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {meaningState === 'error' && (
                <div className="text-sm text-slate-500">
                  Could not fetch meaning.{' '}
                  <button className="text-[#5B8DEF] font-bold underline" onClick={loadMeaning}>Try again</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
